import { camelize, computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, Slot, useId, watch } from 'vue';
import { getElementPath } from '@utils/analytics';
import { VSlotUnwrap } from '@utils/v-slot';

type TabDescriptor = Record<string, any>;

interface UseTabsOptions {
    props: Record<string, any>;
    slots: Record<string, Slot | undefined>;
    emit: (event: string, ...args: any[]) => void;
}

/* Vnode props keep the casing used in the template, so `:anchor-id` never arrives as `anchorId`. */
function withCamelizedKeys(props: TabDescriptor): TabDescriptor {
    const descriptor = { ...props };
    Object.keys(descriptor).forEach((key) => {
        descriptor[camelize(key)] = descriptor[key];
    });
    return descriptor;
}

/** Descriptors of the `a-tab` children, used when the `tabs` prop is not given. */
export function readSlotTabs(slot?: Slot): TabDescriptor[] {
    return VSlotUnwrap(slot)
        .filter((vnode) => typeof vnode?.type === 'object' && vnode.type !== null && (vnode.type as any).name)
        .map((vnode) => withCamelizedKeys(vnode.props || {}));
}

export function normalizeTabs(tabs?: (string | TabDescriptor)[]): TabDescriptor[] {
    return (tabs || []).map((tab) => (typeof tab === 'string' ? { label: tab } : tab || {}));
}

/* Consumers hand over anything from a string to a whole slice descriptor; the numbered
   fallback keeps an unlabelled tab clickable instead of rendering `[object Object]`. */
export function tabLabel(item: any, index: number): string {
    if (typeof item === 'string') return item;
    return item?.text || item?.tab?.label || item?.label || item?.title || `Tab ${index + 1}`;
}

/** Descriptors out of CMS data, where the glyph arrives as a bare name. */
export function toTabDescriptors(items?: any[]): TabDescriptor[] {
    return (items || []).map((item, index) => ({
        label: tabLabel(item, index),
        glyph: item?.glyph ? { name: item.glyph } : null,
    }));
}

export function hashTabIndex(tabs: TabDescriptor[], hash: string): number {
    const anchorId = (hash || '').replace(/^#/, '');
    if (!anchorId) return -1;
    return tabs.findIndex((tab) => tab?.anchorId === anchorId);
}

export function tabKey(tab: TabDescriptor, index: number) {
    return tab?.anchorId || tab?.label || index;
}

/** A tab opts out through its own `event`, the way `a-button` and `a-dynamic-link` are muted. */
export function sendTabMenuEvent(event: Event, tab: TabDescriptor, index: number) {
    if (typeof window === 'undefined' || tab.event?.doNotSendGA) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'Acronis',
        eventCategory: 'Interactions',
        eventAction: 'Tab Menu',
        eventLabel: tab.label,
        eventContent: tab.label,
        eventLocation: getElementPath(event.target as HTMLElement),
        eventButton: (index + 1).toString(),
        ...tab.event,
    });
}

export function tabStorageKey(key: string) {
    return `a-tabs-${key}`;
}

/* `localStorage` throws — rather than returning null — in a private-mode Safari window
   and once the quota is full, so both accesses are guarded. */
function readStoredIndex(key: string): number | null {
    if (typeof window === 'undefined') return null;
    try {
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        const stored = parseInt(localStorage.getItem(key), 10);
        return Number.isInteger(stored) ? stored : null;
    } catch {
        return null;
    }
}

function writeStoredIndex(key: string, index: number) {
    if (typeof window === 'undefined') return;
    try {
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        localStorage.setItem(key, String(index));
    } catch {
        /* Remembering the tab is not worth failing over. */
    }
}

/* Arrow keys follow the reading direction, so they swap meaning inside an RTL subtree. */
function readsRightToLeft(event: KeyboardEvent): boolean {
    const element = (event.currentTarget || event.target) as HTMLElement;
    if (typeof window === 'undefined' || !element?.nodeType) return false;
    return window.getComputedStyle(element).direction === 'rtl';
}

/** Index the WAI-ARIA tab pattern expects for a key, or null when the key is not handled. */
export function keyboardTargetIndex(event: KeyboardEvent, index: number, total: number, isRtl?: boolean): number | null {
    if (!total) return null;
    if (event.key === 'Home') return 0;
    if (event.key === 'End') return total - 1;
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return null;

    const rtl = isRtl ?? readsRightToLeft(event);
    const forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
    return forward ? (index + 1) % total : (index - 1 + total) % total;
}

/**
 * Everything the `a-tabs` variants share: the tab list, the active index and the panel wiring.
 * A variant binds `content` to its panel container, `setTabRef` to its tabs and `tabElementId`
 * to their ids, and watches `tabsSignature` rather than `tabsList` to re-measure itself.
 *
 * `idPrefix` leaves the panels to the consumer; their ids must be `${idPrefix}-panel-${index}`.
 * `controlled` keeps the parent in charge: the component announces the move and waits for
 * `activeTab` to come back, so a parent that may refuse one never falls out of sync.
 * `storageKey` remembers the tab across reloads and is the consumer's to pick — generated ids
 * do not survive a reload, and two strips on a page must not share one.
 *
 * `tab-change` reports every move the component makes, `tab-click` only the ones a click caused.
 * Nothing is emitted while the parent drives `activeTab`, so a binding cannot loop.
 */
export function useTabs({ props, slots, emit }: UseTabsOptions) {
    const instance = getCurrentInstance();
    /* Re-read on change, so a late `v-if` or an async slice still updates the strip. */
    const tabsList = computed(() => normalizeTabs(
        props.tabs?.length ? props.tabs : readSlotTabs(slots.default),
    ));

    /* Every re-read rebuilds the descriptors and `watch` compares by reference, so watchers
       take this instead: it changes only when the strip has to be laid out again. */
    const tabsSignature = computed(() => JSON.stringify(
        tabsList.value.map((tab, index) => [tabKey(tab, index), tab.label, tab.glyph?.name]),
    ));

    const clamp = (index: number) => Math.max(0, Math.min(index, tabsList.value.length - 1));
    /* A negative `activeTab` is how a parent says "none of them" — a stepper past its last step,
       for one. The variants then render without an active tab. */
    const resolveIndex = (index: number) => (index < 0 ? -1 : clamp(index));

    /* The router owns the hash once it is installed; without it the browser is the only source. */
    const routeHash = (instance?.proxy as any)?.$route?.hash;
    const browserHash = typeof window === 'undefined' ? '' : window.location.hash;
    const initialIndex = hashTabIndex(tabsList.value, routeHash ?? browserHash);
    const activeIndex = ref(Number.isInteger(props.activeTab)
        ? resolveIndex(props.activeTab)
        : clamp(Math.max(0, initialIndex)));
    /* The roving tabindex still needs one reachable tab when none of them is active. */
    const focusIndex = computed(() => Math.max(0, activeIndex.value));

    const uid = useId()?.replaceAll(':', '') || '';
    const namespace = computed(() => props.idPrefix || `a-tabs-${uid}`);
    const tabElementId = (index: number) => {
        const generated = `${namespace.value}-tab-${index}`;
        /* An explicit prefix makes the ids a contract with the consumer's own markup. */
        return props.idPrefix ? generated : (tabsList.value[index]?.anchorId || generated);
    };
    const panelElementId = (index: number) => `${namespace.value}-panel-${index}`;

    const storedTabKey = computed(() => (props.storageKey ? tabStorageKey(props.storageKey) : ''));

    const content = ref(null);
    const tabRefs = ref([]);
    const setTabRef = (element, index: number) => {
        if (element) tabRefs.value[index] = element;
    };

    /* Only direct children are matched, otherwise nested `a-tabs` would be toggled too. */
    function syncPanels() {
        const panels = content.value?.querySelectorAll(':scope > [data-tab-panel]') || [];
        panels.forEach((el: HTMLElement, i: number) => {
            const panel = el;
            if (!panel.id) panel.id = panelElementId(i);
            panel.classList.toggle('a-tab-active', i === activeIndex.value);

            /* A panel with no tab to pair with is left alone: an `aria-labelledby` pointing
               at an id that does not exist reads worse than no label at all. */
            if (i >= tabsList.value.length) return;
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('tabindex', '0');
            panel.setAttribute('aria-labelledby', tabElementId(i));
            tabRefs.value[i]?.setAttribute('aria-controls', panel.id);
        });
    }

    function announce(index: number) {
        emit('tab-change', { index, item: tabsList.value[index] });
    }

    /** Returns the index it settled on, which is where the caller should move the focus. */
    function selectTab(index: number) {
        if (!tabsList.value.length) return activeIndex.value;

        const next = clamp(index);
        if (next === activeIndex.value) return next;
        if (!props.controlled) activeIndex.value = next;
        announce(next);
        return next;
    }

    /**
     * Post-mount rather than the initial index: the server has no storage and its HTML is
     * cached, so a stored tab may only be applied on the client. A hash in the address bar
     * wins — a link has to lead where it says it does.
     */
    function restoreStoredTab() {
        if (!storedTabKey.value || initialIndex >= 0) return;

        const stored = readStoredIndex(storedTabKey.value);
        /* Clamping a stale index would activate the last tab instead of the stored one. */
        if (stored === null || stored < 0 || stored >= tabsList.value.length) return;

        /* Announced rather than assigned, so a `controlled` parent stays in charge. */
        selectTab(stored);
    }

    /* Only a hash that belongs to this instance may move it, otherwise any anchor on the
       page would silently reset the tabs to the first one. */
    function handleHashChange() {
        const index = hashTabIndex(tabsList.value, window.location.hash);
        if (index >= 0) selectTab(index);
    }

    watch(() => props.activeTab, (value) => {
        if (!Number.isInteger(value)) return;
        activeIndex.value = resolveIndex(value);
    });

    watch(activeIndex, syncPanels);

    /* Only a settled move is stored, so a refused one is not remembered as if it had happened. */
    watch(activeIndex, (index) => {
        if (storedTabKey.value) writeStoredIndex(storedTabKey.value, index);
    });

    /* A late key still restores, a swapped one does not: that would overwrite a tab the
       user already picked. */
    watch(storedTabKey, (key, prev) => {
        if (key && !prev) restoreStoredTab();
    });

    watch(tabsSignature, () => {
        /* Vue never clears a ref slot here, so a shrinking list would keep detached elements. */
        tabRefs.value.length = tabsList.value.length;
        /* The list shrank past the active tab: fall back to the closest one and announce it,
           or the parent's own `activeTab` would drift — a controlled one included, since the
           index it gave no longer points at anything. An empty list is left alone: nothing
           renders anyway, and a parent holding `activeTab` steady would never restore it. */
        if (tabsList.value.length && activeIndex.value >= tabsList.value.length) {
            activeIndex.value = clamp(activeIndex.value);
            announce(activeIndex.value);
        }
        nextTick(syncPanels);
    });

    onMounted(() => {
        restoreStoredTab();
        nextTick(syncPanels);
        window.addEventListener('hashchange', handleHashChange);
    });

    onBeforeUnmount(() => window.removeEventListener('hashchange', handleHashChange));

    return {
        tabsList,
        tabsSignature,
        activeIndex,
        focusIndex,
        selectTab,
        content,
        tabRefs,
        setTabRef,
        tabElementId,
        panelElementId,
    };
}
